import { useState, useEffect, useCallback } from 'react';
import { Badge } from '../components/ui/badge';
import { CloudCheck, CloudSlash, ArrowsClockwise, X } from '@phosphor-icons/react';

export default function SyncStatusPill({ unsavedCount, isSaving, lastSynced, pendingOps, onRetryAll }) {
  const [online, setOnline] = useState(navigator.onLine);
  const [showSynced, setShowSynced] = useState(false);
  const [showSheet, setShowSheet] = useState(false);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Show "Synced" briefly after a successful sync
  useEffect(() => {
    if (lastSynced && online && unsavedCount === 0 && !isSaving) {
      setShowSynced(true);
      const t = setTimeout(() => setShowSynced(false), 2000);
      return () => clearTimeout(t);
    }
  }, [lastSynced, online, unsavedCount, isSaving]);

  // Offline with pending items
  if (!online && unsavedCount > 0) {
    return (
      <>
        <button onClick={() => setShowSheet(!showSheet)} data-testid="sync-pill-offline"
          className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#E59000]/15 text-[#E59000] text-[10px] font-semibold transition-all">
          <CloudSlash className="w-3 h-3" weight="bold" />
          Offline — {unsavedCount} saved
        </button>
        {showSheet && (
          <PendingSheet ops={pendingOps} onClose={() => setShowSheet(false)} onRetryAll={onRetryAll} />
        )}
      </>
    );
  }

  // Offline but nothing pending
  if (!online) {
    return (
      <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#E59000]/15 text-[#E59000] text-[10px] font-semibold" data-testid="sync-pill-offline">
        <CloudSlash className="w-3 h-3" weight="bold" /> Offline
      </span>
    );
  }

  // Syncing in progress
  if (isSaving) {
    return (
      <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#1B4332]/15 text-[#1B4332] text-[10px] font-semibold" data-testid="sync-pill-syncing">
        <ArrowsClockwise className="w-3 h-3 animate-spin" weight="bold" />
        Syncing{unsavedCount > 0 ? ` ${unsavedCount}` : ''}...
      </span>
    );
  }

  // Unsaved items while online (retrying)
  if (unsavedCount > 0) {
    return (
      <>
        <button onClick={() => setShowSheet(!showSheet)} data-testid="sync-pill-pending"
          className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#E59000]/15 text-[#E59000] text-[10px] font-semibold transition-all">
          <ArrowsClockwise className="w-3 h-3" weight="bold" />
          {unsavedCount} pending
        </button>
        {showSheet && (
          <PendingSheet ops={pendingOps} onClose={() => setShowSheet(false)} onRetryAll={onRetryAll} />
        )}
      </>
    );
  }

  // Synced — green, auto-hides
  if (showSynced) {
    return (
      <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#1B4332]/15 text-[#1B4332] text-[10px] font-semibold animate-fade-in-up" data-testid="sync-pill-synced">
        <CloudCheck className="w-3 h-3" weight="bold" /> Live
      </span>
    );
  }

  return null;
}

function PendingSheet({ ops, onClose, onRetryAll }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="pending-sheet">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl w-full max-w-lg max-h-[50vh] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E1E6E4] flex items-center justify-between">
          <span className="font-semibold text-sm text-[#1D242B]">Pending Operations</span>
          <button onClick={onClose} className="p-1 text-[#455561]"><X className="w-4 h-4" weight="bold" /></button>
        </div>
        <div className="px-4 py-2 max-h-[35vh] overflow-y-auto">
          {(!ops || ops.length === 0) ? (
            <p className="text-xs text-[#B8C7C0] py-4 text-center">No pending operations</p>
          ) : (
            ops.map((op, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-[#F4F6F5] last:border-0">
                <div>
                  <p className="text-xs text-[#1D242B] font-medium">{op.playerName} — Hole {op.hole + 1}</p>
                  <p className="text-[10px] text-[#B8C7C0]">{new Date(op.ts).toLocaleTimeString()}</p>
                </div>
                <Badge className="bg-[#E59000]/15 text-[#E59000] text-[10px]">Retry #{op.retries || 0}</Badge>
              </div>
            ))
          )}
        </div>
        {ops && ops.length > 0 && (
          <div className="px-4 py-3 border-t border-[#E1E6E4]">
            <button onClick={onRetryAll} data-testid="retry-all-btn"
              className="w-full h-10 bg-[#1B4332] text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
              <ArrowsClockwise className="w-4 h-4" weight="bold" /> Retry All
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
